import { api } from '@/lib/api';

/**
 * Meta Embedded Signup API Service
 *
 * Handles communication with backend for WhatsApp Embedded Signup flow
 *
 * Flow:
 * 1. Frontend gets config (appId, configId) from backend
 * 2. Frontend loads Facebook SDK and calls FB.login()
 * 3. User authorizes in Facebook popup
 * 4. Frontend receives code via PostMessage
 * 5. Frontend sends code + phoneNumberId + wabaId to backend
 * 6. Backend exchanges code for token and configures webhook
 */

/**
 * Configuration returned by backend for Facebook SDK initialization
 */
export interface MetaEmbeddedSignupConfig {
  appId: string;           // Meta App ID for FB.init()
  configId: string;        // WhatsApp Embedded Signup Config ID (serve standalone E coexistência — Meta diferencia via featureType em runtime, ver Meta docs Etapa 2)
  coexistenceEnabled?: boolean; // Flag administrativa: quando true, o WhatsAppBusinessForm exibe o picker (standalone vs coexistência). Default off.
  graphApiVersion: string; // Graph API version (e.g., 'v21.0')
}

/**
 * Result from completing the Embedded Signup flow
 */
export interface MetaEmbeddedSignupResult {
  accessToken: string;         // Access token for WhatsApp Business API
  phoneNumberId: string;       // WhatsApp phone number ID
  wabaId: string;              // WhatsApp Business Account ID
  displayPhoneNumber: string;  // Human-readable phone number (e.g., "+55 11 99999-9999")
  webhookConfigured: boolean;  // Whether webhook was auto-configured
  mode?: 'standalone' | 'coexistence';        // Intent — escolha do usuário no onboarding
  accountMode?: 'STANDALONE' | 'COEXISTENCE'; // Truth — confirmado pela Meta via Graph API
}

/**
 * Request to complete Embedded Signup
 */
export interface CompleteEmbeddedSignupRequest {
  code: string;           // OAuth authorization code from FB.login()
  phoneNumberId: string;  // Phone number ID from PostMessage
  wabaId: string;         // WABA ID from PostMessage
  mode?: 'standalone' | 'coexistence'; // Optional — choice made in the form
}

export const metaApi = {
  /**
   * Get Meta Embedded Signup configuration for frontend SDK
   *
   * Returns appId, configId, and graphApiVersion needed to:
   * 1. Initialize Facebook SDK with FB.init()
   * 2. Start Embedded Signup with FB.login()
   *
   * @returns MetaEmbeddedSignupConfig
   */
  async getEmbeddedSignupConfig(): Promise<MetaEmbeddedSignupConfig> {
    const response = await api.get('provider/meta/embedded-signup-config');
    return response.data.data;
  },

  /**
   * Complete WhatsApp Embedded Signup flow
   *
   * After user authorizes in Facebook popup and we receive the code
   * via PostMessage, this method:
   * 1. Sends code to backend
   * 2. Backend exchanges code for access token
   * 3. Backend configures webhook for WABA
   * 4. Returns credentials for channel creation
   *
   * @param data - Code, phoneNumberId, and wabaId from PostMessage
   * @returns MetaEmbeddedSignupResult with access token and config status
   */
  async completeEmbeddedSignup(
    data: CompleteEmbeddedSignupRequest
  ): Promise<MetaEmbeddedSignupResult> {
    const response = await api.post('provider/meta/embedded-signup/complete', data);
    return response.data.data;
  },

};
